import { useState, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { useAuth } from '../hooks/useAuthHook'
import { cn } from '../lib/utils'
import { Input } from '../components/ui/Input'
import { Button } from '../components/ui/Button'
import { useToast } from '../components/ui/useToast'

export function RegisterPage() {
  const navigate = useNavigate()
  const { register, isAuthenticated, isLoading } = useAuth()
  const { notify } = useToast()
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
    tenant_name: '',
  })
  const [errors, setErrors] = useState<Record<string, string>>({})

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/dashboard')
    }
  }, [isAuthenticated, navigate])

  const validateForm = () => {
    const newErrors: Record<string, string> = {}

    if (!formData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!formData.email.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    }

    if (!formData.password) {
      newErrors.password = 'Password is required'
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters'
    }

    if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match'
    }

    if (!formData.tenant_name.trim()) {
      newErrors.tenant_name = 'Studio/Company name is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validateForm()) return

    try {
      await register({
        name: formData.name,
        email: formData.email,
        password: formData.password,
        tenant_name: formData.tenant_name,
      })
      notify({ kind: 'success', title: 'Account created', message: 'Welcome! Your account is ready.' })
      navigate('/dashboard')
    } catch (err: any) {
      const backendErrors = err.response?.data?.errors
      if (backendErrors && typeof backendErrors === 'object') {
        // Map array-shaped errors to first string, keep strings as-is
        const mapped: Record<string, string> = {}
        for (const key of Object.keys(backendErrors)) {
          const val = backendErrors[key]
          mapped[key] = Array.isArray(val) ? String(val[0]) : String(val)
        }
        setErrors(mapped)
      } else {
        setErrors({ general: err.message || 'Registration failed. Please try again.' })
      }
    }
  }

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target
    setFormData(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  return (
    <div className="min-h-screen grid md:grid-cols-2 bg-gray-50 dark:bg-gray-950">
      {/* Left: Branding */}
      <div className="relative hidden md:flex flex-col p-10 bg-gradient-to-br from-primary-600 via-primary-700 to-accent-600 text-white overflow-hidden">
        <div className="pointer-events-none absolute inset-0 gradient-mesh opacity-30" />
        {/* Top brand */}
        <div className="relative z-10 flex items-center gap-3">
          <div className="w-12 h-12 rounded-xl bg-white/10 backdrop-blur-sm flex items-center justify-center shadow-soft-lg">
            <div className="w-10 h-10 rounded-lg bg-gradient-to-br from-white to-primary-100 flex items-center justify-center text-primary-600 font-bold text-lg">SM</div>
          </div>
          <div className="text-xl font-bold">studiomgr360</div>
        </div>
        {/* Centered hero content */}
        <div className="relative z-10 flex-1 flex items-center">
          <div className="max-w-md space-y-6">
            <h2 className="text-4xl md:text-5xl font-bold tracking-tight leading-tight">Create your account and start today.</h2>
            <p className="text-lg text-white/90 leading-relaxed">Set up your studio in minutes. Flexible, secure, and ready to scale with your business.</p>
            <div className="flex items-center gap-6 pt-4">
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">Quick setup</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">No credit card</span>
              </div>
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-white"></div>
                <span className="text-sm text-white/80">Free trial</span>
              </div>
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="relative z-10 text-sm text-white/70">© {new Date().getFullYear()} studiomgr360. All rights reserved.</div>
      </div>

      {/* Right: Form */}
      <div className="flex items-center justify-center px-6 py-12 md:px-12 bg-white dark:bg-gray-900">
        <div className="w-full max-w-md animate-fade-slide-up">
          <div className="mb-8">
            <div className="md:hidden text-center mb-6">
              <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center mx-auto mb-3 shadow-soft">
                <span className="text-white font-bold text-lg">SM</span>
              </div>
              <div className="text-2xl font-bold text-gray-900 dark:text-white">studiomgr360</div>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900 dark:text-white">Get started</h1>
            <p className="mt-2 text-gray-600 dark:text-gray-400">Create your account to manage your studio</p>
          </div>
          {errors.general && (
            <div className="mb-6 bg-error-50 dark:bg-error-500/10 border border-error-200 dark:border-error-500/20 text-error-700 dark:text-error-400 px-4 py-3 rounded-xl">
              {errors.general}
            </div>
          )}
          <form className="space-y-5" onSubmit={handleSubmit}>
            <div>
              <label htmlFor="tenant_name" className="form-label">Studio/Company Name *</label>
              <Input id="tenant_name" name="tenant_name" type="text" required placeholder="Enter your studio name" value={formData.tenant_name} onChange={handleChange} />
              {errors.tenant_name && (<p className="form-error">{errors.tenant_name}</p>)}
            </div>
            <div>
              <label htmlFor="name" className="form-label">Your Full Name *</label>
              <Input id="name" name="name" type="text" required placeholder="Enter your full name" value={formData.name} onChange={handleChange} />
              {errors.name && (<p className="form-error">{errors.name}</p>)}
            </div>
            <div>
              <label htmlFor="email" className="form-label">Email Address *</label>
              <Input id="email" name="email" type="email" required placeholder="Enter your email" value={formData.email} onChange={handleChange} />
              {errors.email && (<p className="form-error">{errors.email}</p>)}
            </div>
            <div>
              <label htmlFor="password" className="form-label">Password *</label>
              <Input id="password" name="password" type="password" required placeholder="Create a password (min 8 characters)" value={formData.password} onChange={handleChange} />
              {errors.password && (<p className="form-error">{errors.password}</p>)}
            </div>
            <div>
              <label htmlFor="confirmPassword" className="form-label">Confirm Password *</label>
              <Input id="confirmPassword" name="confirmPassword" type="password" required placeholder="Confirm your password" value={formData.confirmPassword} onChange={handleChange} />
              {errors.confirmPassword && (<p className="form-error">{errors.confirmPassword}</p>)}
            </div>
            <Button type="submit" disabled={isLoading} className={cn('w-full h-12 text-base', isLoading && 'opacity-50 cursor-not-allowed')}>
              {isLoading ? 'Creating account...' : 'Create account'}
            </Button>
          </form>
          <div className="mt-6 text-center text-sm text-gray-600 dark:text-gray-400">
            Already have an account?{' '}
            <Link to="/login" className="text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium transition-colors">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  )
}